import { createContext, useContext, useEffect, useState } from "react";

import {
  TTheme,
  TThemeProviderProps,
  TThemeProviderState,
} from "@/types/public";

const initialState: TThemeProviderState = {
  theme: "system",
  setTheme: () => null,
};

const ThemeProviderContext = createContext<TThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "vite-ui-theme",
  ...props
}: TThemeProviderProps) {
  // ============= State ============
  const [theme, setTheme] = useState<TTheme>(
    () => (localStorage.getItem(storageKey) as TTheme) || defaultTheme
  );

  // ============= Effect ============
  useEffect(() => {
    const root = window.document.documentElement;
    const previousTheme = root.classList.contains("dark") ? "dark" : "light";
    if (previousTheme) root.classList.remove(previousTheme);
    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      if (theme === "system") {
        const systemTheme = e.matches ? "dark" : "light";
        root.classList.replace(previousTheme, systemTheme);
      }
    };
    if (theme === "system")
      mediaQuery.addEventListener("change", handleSystemThemeChange);
    return () => {
      if (theme === "system")
        mediaQuery.removeEventListener("change", handleSystemThemeChange);
    };
  }, [theme]);

  const value = {
    theme,
    setTheme: (theme: TTheme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
  };

  // ============= Rendering ============
  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");
  return context;
};
